import { LitElement, type PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { bound } from '@patternfly/pfe-core/decorators.js';
import type {
  ClientParams, OidcManager
} from '@rhitm-incubator/oidc-manager';
import { oidcManagerResolved } from '../../lib/oidcManagerResolved.js';
import styles from './rhpt-sso-login.css';

@customElement('rhpt-sso-login')
export class RhptSsoLogin extends LitElement {
  static styles = [styles];

  /*
   * This is a state property that will be used to determine if the user is authenticated anywhere on the page.
   * It is not dependent on the client_id or redirect_uri.
   *
   * This is what determines what text is displayed in the button / link.
   */
  @state()
    authenticated = false;

  /*
   * These are properties that are used to configure the SSO service.
   * client_id is the client_id of the application that is being used to authenticate the user.
   * authority is the URL of the SSO server that the client_id is registered with.
   */
  @property({ attribute: 'client-id' })
    client_id?: string;

  @property()
    authority?: string;

  /*
   * This is the redirect_uri that the user will be sent to after they have authenticated.
   */
  @property({ attribute: 'redirect-uri' })
    redirect_uri?: string;


  /**
   * Selector that will trigger the login action
   */
  @property()
    trigger?: string;

  /**
   * Used to clean up event listeners when
   * the element is removed
   */
  #abortController = new AbortController();

  /**
   * Used to clean up event listeners when
   * the element is removed
   */
  #triggerAbortController = new AbortController();

  connectedCallback(): void {
    super.connectedCallback();
    this.#init();
  }

  protected updated(changedProperties: PropertyValueMap<typeof this>): void {
    if (changedProperties.has('trigger')) {
      this.#triggerChanged();
    }
  }

  /**
   * Fired when the `trigger` value changes
   */
  #triggerChanged() {
    if (!this.trigger) { return; }
    // add click event listener to the trigger element
    // if found.
    document.querySelector(this.trigger)
      ?.addEventListener('click', this._triggerClickHandler, {
        signal: this.#triggerAbortController.signal
      });
  }

  /**
   * Callback handler when the specified trigger
   * element is clicked
   */
  @bound
  private _triggerClickHandler(e: Event) {
    const trigger = e.composedPath()?.[0];
    if (!trigger) { return; }

    // if we are in an unauthenticated state
    // then we need to login using OIDC Manager.
    if (!this.authenticated) {
      e.preventDefault();

      // use href as redirect uri
      if (
        // is an anchor tag
        trigger instanceof HTMLAnchorElement &&
        // has a valid href
        trigger.href &&
        // no redirect_uri specified
        !this.redirect_uri
      ) {
        this.redirect_uri = trigger.href;
      }

      this.#login();
    }
  }

  /**
   * Initializes the component with OIDC Manager.
   */
  async #init() {
    // wait until the OIDC Manager singleton is ready
    await oidcManagerResolved;
    // see if there are any valid client users already
    this._checkAuthentication();
    // listen for future messages from OIDC Manager
    this.#registerEventListener();
  }

  /*
   * This method is used to check if the user is authenticated.
   * It is called when the oidcManager sends a message.
   * If the user is authenticated on any client on the page, the authenticated property is set to true.
   */
  @bound
  _checkAuthentication() {
    this.authenticated = window.oidcManager.getValidClientUsers().length > 0;
  }

  /*
   * This method is used to login the user.
   * It is called when the login button is clicked.
   * It calls the login method on the SSO service client.
   */
  #login() {
    if (!window.oidcManager || !this.client_id || !this.authority || !this.redirect_uri) {
      console.warn(`The oidcManager is not loaded or the client_id, authority, or redirect_uri is not set.`);
      return;
    }
    const client = window.oidcManager.createClient({
      client_id: this.client_id,
      authority: this.authority,
      redirect_uri: this.redirect_uri ?? window.location.href,
      force_redirect: true,
    });
    client.login();
  }

  /*
   * This method is used to register an event listener on the oidcManager.
   * It is called when the oidcManager is loaded.
   */
  #registerEventListener() {
    window.oidcManager.addEventListener('message', this._checkAuthentication, {
      signal: this.#abortController.signal
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#abortController.abort();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rhpt-sso-login': RhptSsoLogin;
  }
  interface Window {
    /**
     * oidcManager provides SSO client communication for a consistent user experience.
     */
    oidcManager: OidcManager;
    drupalSettings?: {
      rh_oidc_manager: {
        oidc_settings: ClientParams;
      };
    };
  }
}

